import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  CircleHalf,
  GridFour,
  Sun,
  Palette,
  Camera,
  Sliders,
  Printer,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const ICONS = { CircleHalf, GridFour, Sun, Palette, Camera, Sliders };

const CONTENT = {
  en: {
    page_title: "Black & White Photography",
    page_sub: "Strip away color and you're left with the raw bones of an image: light, shadow, shape, and texture. Black & white forces you to compose with tone and contrast instead of leaning on bright hues — the discipline that built the foundation of photography and still produces its most timeless, emotional images.",
    download: "Download as PDF",
    generating: "Generating...",
    pro_tip_label: "Pro tip",

    sections: [
      {
        id: "why-bw",
        icon: "CircleHalf",
        title: "1. Why Shoot Black & White?",
        intro: "Removing color removes a distraction. When the viewer can't be pulled around by a red jacket or a blue sky, attention falls on what really matters: composition, light, gesture, and emotion. B&W is not 'a photo with the saturation turned down' — it's a different language with its own rules.",
        steps: [
          "Composition first: shape, line, and form carry the frame, so weak compositions have nowhere to hide.",
          "Emotion and timelessness: monochrome reads as classic and editorial; it ages slowly and feels intentional.",
          "Light becomes the subject: you photograph how light falls, not what color things are.",
          "Mood and drama: deep blacks and bright highlights create tension that color often softens.",
          "Unifies messy scenes: clashing colors that ruin a color shot can become a clean tonal study in B&W.",
        ],
        tip: "Before you shoot, ask: 'Does the color add to this image, or is the story really about light, shape, and contrast?' If it's the latter, you have a black & white photo.",
      },
      {
        id: "tonal-range",
        icon: "GridFour",
        title: "2. Tonal Range & the Zone System Basics",
        intro: "Tonal range is the spread of brightness from pure black to pure white, and everything in between. Ansel Adams' Zone System is a simple mental map of that range — 11 zones (0–X) — that helps you decide where each part of a scene should sit and expose with intent.",
        steps: [
          "Zone 0 = pure black (no detail). Zone X = pure white (no detail). Zone V is middle gray — exactly what your meter assumes.",
          "Zones III–VII hold most usable detail: dark shadows with texture (III) up to bright highlights with texture (VII).",
          "Your light meter always tries to make whatever it reads into Zone V (18% gray) — that's why snow looks gray and coal looks gray if you trust it blindly.",
          "'Place' a tone: meter an important area, then add or subtract stops to put it where you want (e.g. meter white snow and add +2 stops to render it bright at Zone VII, not gray).",
          "A great B&W image usually uses the FULL range: a true black somewhere, a clean white somewhere, and rich mid-tones connecting them.",
        ],
        tip: "Expose for the highlights, develop/edit for the shadows. Once highlights are blown to pure white they're gone forever, but shadow detail can usually be lifted — so protect the bright end first.",
      },
      {
        id: "contrast",
        icon: "Sun",
        title: "3. Contrast, Shadows & Highlights as Subject Matter",
        intro: "In color, contrast is one ingredient. In black & white, contrast IS the image. The relationship between light and dark areas creates depth, mood, and the very lines your eye follows. Learn to see and shape it deliberately.",
        steps: [
          "Direction of light matters most: side light rakes across surfaces and reveals texture; flat front light kills it.",
          "High contrast (deep shadows, bright highlights) = drama, grit, graphic punch. Great for street, architecture, bold portraits.",
          "Low contrast (soft, gray, gentle) = mood, fog, quiet, delicate skin and detail. Great for misty landscapes and subtle portraits.",
          "Use shadow shapes as composition: a cast shadow can be the leading line or even the main subject.",
          "Negative space: large areas of pure black or white give the eye rest and make the subject pop.",
          "Watch your highlights for shape — a rim of light on a shoulder or a bright window can define the whole frame.",
        ],
        tip: "Squint at the scene (or your screen). Squinting collapses color and detail into pure tonal masses — exactly how a B&W image will read. If it works squinting, it'll work in monochrome.",
      },
      {
        id: "filters",
        icon: "Sliders",
        title: "4. Contrast Filters (Glass or Digital)",
        intro: "Colored filters were the classic way to control how colors translate into gray tones — and the same effect is built into every RAW B&W conversion today. A filter lightens its own color and darkens the opposite color.",
        steps: [
          "Yellow: subtle, natural-looking. Slightly darkens blue skies so clouds separate. The everyday default.",
          "Orange: stronger sky darkening, smooths skin blemishes, boosts haze penetration. Great for portraits and landscapes.",
          "Red: dramatic near-black skies and brilliant white clouds; renders foliage dark. The 'epic landscape' look.",
          "Green: lightens foliage and darkens skin slightly — useful for nature and some male portraits.",
          "In digital: these live in the B&W conversion panel as color sliders — drag the Blue slider down to darken a sky, exactly like a red filter.",
        ],
        tip: "Don't overdo the red-filter look — pushing the sky to total black can look fake and crush cloud detail. A little goes a long way.",
      },
      {
        id: "when-color",
        icon: "Palette",
        title: "5. When Color Helps vs. When It Hurts",
        intro: "Black & white is a choice, not a default. Some images are ABOUT color and lose their meaning without it; others are buried by color and set free in monochrome. Knowing the difference is the mark of a thoughtful photographer.",
        steps: [
          "Shoot B&W when: the story is light/shadow/texture, the colors are dull or clashing, or you want a timeless, emotional, documentary feel.",
          "Keep color when: color IS the subject (a sunset, autumn leaves, a vivid market, food), or color carries the meaning/branding.",
          "Test it: glance at your shot in both. If B&W makes it stronger and more intentional, commit. If it feels like information is missing, keep color.",
          "Consistency: in a series or portfolio, mixing color and B&W randomly looks unplanned — choose a deliberate visual language.",
        ],
        tip: "A useful rule from photojournalism: 'B&W is about the moment and the form; color is about the place and the mood.' Decide which one your image is really about.",
      },
      {
        id: "converting",
        icon: "Camera",
        title: "6. Converting From Color vs. Shooting Native B&W",
        intro: "You can create black & white two ways: capture in color and convert later, or set your camera to monochrome and commit in the moment. Each has a place — and the smartest workflow combines them.",
        steps: [
          "Best of both worlds: shoot RAW and set your camera's Picture Style/Profile to Monochrome. The rear screen and EVF preview in B&W (training your eye), but the RAW file keeps ALL color data for a flexible conversion.",
          "JPEG-only monochrome = locked in: if you shoot JPEG in mono, you cannot recover the color — only do this when you're sure.",
          "Converting in editing: never just desaturate. Use a dedicated B&W conversion with per-color sliders to sculpt the tones (darken skies, brighten skin, separate similar tones).",
          "Add contrast and clarity thoughtfully, set a true black point and white point, and consider light grain for a filmic feel.",
          "Native B&W discipline: shooting mono in-camera forces you to previsualize tones on the spot — a fantastic training exercise even if you later edit the RAW.",
        ],
        tip: "Set up a custom 'Monochrome' Picture Style with a slightly raised contrast and a yellow/orange filter effect. You'll see dramatic B&W on the screen while keeping the safety of the full-color RAW underneath.",
      },
    ],

    filters_table_title: "Contrast Filter Quick Reference",
    filters_table_sub: "How a colored filter (or its digital slider equivalent) changes gray tones.",
    ft_h_filter: "Filter",
    ft_h_lightens: "Lightens",
    ft_h_darkens: "Darkens",
    ft_h_use: "Classic use",
    filters_rows: [
      { filter: "Yellow", lightens: "Yellows, skin", darkens: "Blue sky (slightly)", use: "Natural everyday look" },
      { filter: "Orange", lightens: "Skin, warm tones", darkens: "Blue sky (more), haze", use: "Portraits + landscapes" },
      { filter: "Red", lightens: "Reds, warm tones", darkens: "Blue sky (dramatic), foliage", use: "Epic, high-drama skies" },
      { filter: "Green", lightens: "Foliage, greens", darkens: "Skin, reds", use: "Nature, some portraits" },
    ],

    closing: "Black & white never goes out of style because it speaks the original language of photography: light and shadow. Train your eye to see in tones, expose with intent, and shape your contrast deliberately — and you'll make images that feel timeless in any era.",
  },

  es: {
    page_title: "Fotografía en Blanco y Negro",
    page_sub: "Quita el color y te quedas con el esqueleto puro de una imagen: luz, sombra, forma y textura. El blanco y negro te obliga a componer con tono y contraste en lugar de apoyarte en colores llamativos — la disciplina que construyo los cimientos de la fotografía y que aún produce sus imágenes más atemporales y emotivas.",
    download: "Descargar como PDF",
    generating: "Generando...",
    pro_tip_label: "Tip pro",

    sections: [
      {
        id: "why-bw",
        icon: "CircleHalf",
        title: "1. Por Que Disparar en Blanco y Negro?",
        intro: "Quitar el color quita una distracción. Cuando al espectador no lo arrastra una chaqueta roja o un cielo azul, la atención cae en lo que de verdad importa: composición, luz, gesto y emoción. El B/N no es 'una foto con la saturación bajada' — es un lenguaje distinto con sus propias reglas.",
        steps: [
          "Composición primero: forma, línea y volumen sostienen el encuadre, así que las composiciones débiles no tienen donde esconderse.",
          "Emoción y atemporalidad: el monocromo se lee como clásico y editorial; envejece despacio y se siente intencional.",
          "La luz se vuelve el sujeto: fotografías como cae la luz, no de que color son las cosas.",
          "Ambiente y drama: negros profundos y altas luces brillantes crean una tensión que el color suele suavizar.",
          "Unifica escenas caoticas: colores que chocan y arruinan una foto a color pueden volverse un limpio estudio tonal en B/N.",
        ],
        tip: "Antes de disparar, preguntate: '¿El color suma a esta imagen, o la historia es realmente sobre luz, forma y contraste?' Si es lo segundo, tienes una foto en blanco y negro.",
      },
      {
        id: "tonal-range",
        icon: "GridFour",
        title: "2. Rango Tonal y Bases del Sistema de Zonas",
        intro: "El rango tonal es la extensión del brillo desde el negro puro hasta el blanco puro, y todo lo que hay en medio. El Sistema de Zonas de Ansel Adams es un mapa mental simple de ese rango — 11 zonas (0–X) — que te ayuda a decidir donde debe quedar cada parte de una escena y a exponer con intención.",
        steps: [
          "Zona 0 = negro puro (sin detalle). Zona X = blanco puro (sin detalle). La Zona V es el gris medio — exactamente lo que asume tu fotómetro.",
          "Las zonas III–VII tienen casi todo el detalle útil: sombras oscuras con textura (III) hasta altas luces brillantes con textura (VII).",
          "Tu fotómetro siempre intenta convertir lo que mide en Zona V (gris 18%) — por eso la nieve sale gris y el carbon sale gris si confías ciegamente.",
          "'Coloca' un tono: mide una zona importante y suma o resta pasos para ponerla donde quieres (ej.: mide nieve blanca y suma +2 pasos para que salga brillante en Zona VII, no gris).",
          "Una gran imagen B/N suele usar el rango COMPLETO: un negro real en algún punto, un blanco limpio en otro, y medios tonos ricos que los conectan.",
        ],
        tip: "Expón para las altas luces, revela/edita para las sombras. Una vez quemadas las altas luces a blanco puro, se pierden para siempre, pero el detalle de sombra suele poder levantarse — protege primero la zona brillante.",
      },
      {
        id: "contrast",
        icon: "Sun",
        title: "3. Contraste, Sombras y Altas Luces como Tema",
        intro: "En color, el contraste es un ingrediente. En blanco y negro, el contraste ES la imagen. La relación entre zonas claras y oscuras crea profundidad, ambiente y las propias líneas que sigue tu ojo. Aprende a verlo y moldearlo a propósito.",
        steps: [
          "La dirección de la luz es lo más importante: la luz lateral roza las superficies y revela textura; la luz frontal plana la mata.",
          "Alto contraste (sombras profundas, altas luces brillantes) = drama, aspereza, fuerza gráfica. Ideal para calle, arquitectura y retratos potentes.",
          "Bajo contraste (suave, gris, delicado) = ambiente, niebla, calma, piel y detalle delicados. Ideal para paisajes con bruma y retratos sutiles.",
          "Usa las formas de las sombras como composición: una sombra proyectada puede ser la línea guía o incluso el sujeto principal.",
          "Espacio negativo: grandes areas de negro o blanco puro dan descanso al ojo y hacen resaltar al sujeto.",
          "Observa la forma de tus altas luces — un borde de luz en un hombro o una ventana brillante puede definir todo el encuadre.",
        ],
        tip: "Entrecierra los ojos ante la escena (o tu pantalla). Al entrecerrar, el color y el detalle se colapsan en masas tonales puras — exactamente como se leera la imagen en B/N. Si funciona entrecerrando, funcionara en monocromo.",
      },
      {
        id: "filters",
        icon: "Sliders",
        title: "4. Filtros de Contraste (Vidrio o Digital)",
        intro: "Los filtros de color eran la forma clásica de controlar como se traducen los colores en tonos de gris — y el mismo efecto esta hoy integrado en cada conversión B/N de RAW. Un filtro aclara su propio color y oscurece el color opuesto.",
        steps: [
          "Amarillo: sutil y de aspecto natural. Oscurece ligeramente el cielo azul para que las nubes se separen. El predeterminado del día a día.",
          "Naranja: oscurece más el cielo, suaviza imperfecciones de la piel, atraviesa mejor la bruma. Genial para retratos y paisajes.",
          "Rojo: cielos casi negros dramaticos y nubes blancas brillantes; oscurece el follaje. El look de 'paisaje epico'.",
          "Verde: aclara el follaje y oscurece un poco la piel — útil para naturaleza y algunos retratos masculinos.",
          "En digital: viven en el panel de conversión B/N como deslizadores de color — baja el deslizador Azul para oscurecer un cielo, igual que un filtro rojo.",
        ],
        tip: "No exageres el look de filtro rojo — llevar el cielo a negro total puede verse falso y aplastar el detalle de las nubes. Un poco rinde mucho.",
      },
      {
        id: "when-color",
        icon: "Palette",
        title: "5. Cuando el Color Ayuda vs. Cuando Estorba",
        intro: "El blanco y negro es una elección, no un predeterminado. Algunas imágenes TRATAN sobre el color y pierden su sentido sin el; otras quedan sepultadas por el color y se liberan en monocromo. Saber la diferencia es la marca de un fotógrafo reflexivo.",
        steps: [
          "Dispara B/N cuando: la historia es luz/sombra/textura, los colores son apagados o chocan, o quieres un aire atemporal, emotivo y documental.",
          "Conserva el color cuando: el color ES el sujeto (un atardecer, hojas de otoño, un mercado vivido, comida), o el color carga el significado/la marca.",
          "Pruebalo: mira tu toma en ambos. Si el B/N la hace más fuerte e intencional, comprometete. Si sientes que falta información, conserva el color.",
          "Coherencia: en una serie o portafolio, mezclar color y B/N al azar parece no planeado — elige un lenguaje visual deliberado.",
        ],
        tip: "Una regla útil del fotoperiodismo: 'El B/N trata sobre el momento y la forma; el color trata sobre el lugar y el ambiente.' Decide de cual trata realmente tu imagen.",
      },
      {
        id: "converting",
        icon: "Camera",
        title: "6. Convertir desde Color vs. Disparar B/N Nativo",
        intro: "Puedes crear blanco y negro de dos formas: capturar en color y convertir después, o configurar la cámara en monocromo y comprometerte en el momento. Cada una tiene su lugar — y el flujo más inteligente las combina.",
        steps: [
          "Lo mejor de ambos mundos: dispara RAW y pon el Estilo de Imagen/Perfil de tu cámara en Monocromo. La pantalla y el visor muestran B/N (entrenando tu ojo), pero el RAW conserva TODOS los datos de color para una conversión flexible.",
          "Monocromo solo en JPEG = irreversible: si disparas JPEG en mono, no puedes recuperar el color — hazlo solo cuando estés seguro.",
          "Convertir en edición: nunca solo desatures. Usa una conversión B/N dedicada con deslizadores por color para esculpir los tonos (oscurecer cielos, aclarar piel, separar tonos similares).",
          "Añade contraste y claridad con criterio, fija un punto negro y un punto blanco reales, y considera un grano ligero para un aire filmico.",
          "Disciplina del B/N nativo: disparar mono en cámara te obliga a previsualizar los tonos en el sitio — un ejercicio de entrenamiento fantastico aunque luego edites el RAW.",
        ],
        tip: "Configura un Estilo de Imagen 'Monocromo' personalizado con contraste algo elevado y un efecto de filtro amarillo/naranja. Verás un B/N dramático en la pantalla mientras mantienes la seguridad del RAW a todo color debajo.",
      },
    ],

    filters_table_title: "Referencia Rápida de Filtros de Contraste",
    filters_table_sub: "Como un filtro de color (o su deslizador digital equivalente) cambia los tonos de gris.",
    ft_h_filter: "Filtro",
    ft_h_lightens: "Aclara",
    ft_h_darkens: "Oscurece",
    ft_h_use: "Uso clásico",
    filters_rows: [
      { filter: "Amarillo", lightens: "Amarillos, piel", darkens: "Cielo azul (poco)", use: "Aspecto natural diario" },
      { filter: "Naranja", lightens: "Piel, tonos cálidos", darkens: "Cielo azul (más), bruma", use: "Retratos + paisajes" },
      { filter: "Rojo", lightens: "Rojos, tonos cálidos", darkens: "Cielo azul (dramático), follaje", use: "Cielos epicos de alto drama" },
      { filter: "Verde", lightens: "Follaje, verdes", darkens: "Piel, rojos", use: "Naturaleza, algunos retratos" },
    ],

    closing: "El blanco y negro nunca pasa de moda porque habla el lenguaje original de la fotografía: luz y sombra. Entrena tu ojo para ver en tonos, expón con intención y moldea tu contraste a propósito — y harás imágenes que se sienten atemporales en cualquier época.",
  },
};

function LessonCard({ section, tipLabel }) {
  const Icon = ICONS[section.icon] || CircleHalf;
  return (
    <motion.div
      id={section.id}
      variants={itemAnim}
      className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-4 scroll-mt-24"
      data-testid={`bw-section-${section.id}`}
    >
      <div className="flex items-center gap-2">
        <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
        <h2 className="font-['Outfit'] text-white text-xl font-medium">{section.title}</h2>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed">{section.intro}</p>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
        <ol className="space-y-1.5">
          {section.steps.map((s, i) => (
            <li key={i} className="text-[#A1A1AA] text-xs leading-relaxed flex gap-2">
              <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-lg p-3">
        <p className="text-[#F5A524] text-xs italic leading-relaxed">
          <span className="font-semibold uppercase tracking-widest mr-1.5">{tipLabel}:</span>
          {section.tip}
        </p>
      </div>
    </motion.div>
  );
}

export default function BlackWhitePage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `black-and-white-${lang}.pdf`, [lang]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="bw-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <CircleHalf weight="fill" className="w-6 h-6 text-[#F5A524]" />
                <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight">{c.page_title}</h1>
              </div>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="bw-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Quick jump grid */}
          <motion.div variants={itemAnim} className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="bw-subnav">
            {c.sections.map((s) => {
              const Icon = ICONS[s.icon] || CircleHalf;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="bg-[#121214]/60 backdrop-blur-md border border-white/10 hover:border-[#F5A524]/40 rounded-lg p-3 flex items-start gap-2 transition-colors group"
                  data-testid={`bw-jump-${s.id}`}
                >
                  <Icon weight="fill" className="w-4 h-4 text-[#F5A524] shrink-0 mt-0.5" />
                  <span className="text-[#A1A1AA] text-xs leading-snug group-hover:text-white transition-colors">{s.title}</span>
                </a>
              );
            })}
          </motion.div>

          {c.sections.map((s) => (
            <LessonCard key={s.id} section={s} tipLabel={c.pro_tip_label} />
          ))}

          {/* Contrast filter reference table */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-3" data-testid="bw-filters-table">
            <div className="flex items-center gap-2">
              <Sliders weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.filters_table_title}</h2>
            </div>
            <p className="text-[#71717A] text-sm">{c.filters_table_sub}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#71717A] text-xs uppercase tracking-widest">
                    <th className="py-2 pr-3 font-medium">{c.ft_h_filter}</th>
                    <th className="py-2 pr-3 font-medium">{c.ft_h_lightens}</th>
                    <th className="py-2 pr-3 font-medium">{c.ft_h_darkens}</th>
                    <th className="py-2 font-medium">{c.ft_h_use}</th>
                  </tr>
                </thead>
                <tbody>
                  {c.filters_rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="py-2 pr-3 text-white font-medium">{r.filter}</td>
                      <td className="py-2 pr-3 text-[#A1A1AA]">{r.lightens}</td>
                      <td className="py-2 pr-3 text-[#A1A1AA]">{r.darkens}</td>
                      <td className="py-2 text-[#A1A1AA]">{r.use}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#0A0A0A] border border-[#F5A524]/20 rounded-xl p-6">
            <p className="text-[#A1A1AA] text-sm leading-relaxed italic">{c.closing}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print-only clean PDF layout */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="bw-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-6 text-sm">{c.page_sub}</p>

        {c.sections.map((s) => (
          <div key={s.id} className="mb-5">
            <h2 className="text-xl font-bold mb-1 border-b-2 border-gray-200 pb-1">{s.title}</h2>
            <p className="text-sm text-gray-700 mb-2">{s.intro}</p>
            <ol className="list-decimal ml-5 mb-2">
              {s.steps.map((step, i) => (
                <li key={i} className="text-xs text-gray-700 mb-0.5">{step}</li>
              ))}
            </ol>
            <p className="text-xs text-gray-700 italic"><strong>{c.pro_tip_label}:</strong> {s.tip}</p>
          </div>
        ))}

        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-1">{c.filters_table_title}</h2>
        <table className="w-full text-xs border-collapse mb-4">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-1 text-left">{c.ft_h_filter}</th>
              <th className="border border-gray-300 p-1 text-left">{c.ft_h_lightens}</th>
              <th className="border border-gray-300 p-1 text-left">{c.ft_h_darkens}</th>
              <th className="border border-gray-300 p-1 text-left">{c.ft_h_use}</th>
            </tr>
          </thead>
          <tbody>
            {c.filters_rows.map((r, i) => (
              <tr key={i}>
                <td className="border border-gray-300 p-1 font-semibold">{r.filter}</td>
                <td className="border border-gray-300 p-1">{r.lightens}</td>
                <td className="border border-gray-300 p-1">{r.darkens}</td>
                <td className="border border-gray-300 p-1">{r.use}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="text-sm text-gray-700 italic mb-4">{c.closing}</p>
        <p className="text-center text-xs text-gray-400 mt-6">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
